// src/components/SaveStatusIndicator.jsx
// Visual indicator for album save status

import React from 'react';

/**
 * SaveStatusIndicator - Shows current save status
 * 
 * Props:
 * - status: 'idle' | 'saving' | 'saved' | 'error'
 * - progress: { phase, done, total } or null
 * - position: 'bottom-right' | 'top-right' | 'inline'
 */
export default function SaveStatusIndicator({ 
  status = 'idle', 
  progress = null,
  position = 'bottom-right',
  className = ''
}) {
  // Don't show anything when idle
  if (status === 'idle' && !progress) return null;
  
  const positionStyles = {
    'bottom-right': {
      position: 'fixed',
      bottom: 20,
      right: 20,
      zIndex: 9999
    },
    'top-right': {
      position: 'fixed',
      top: 20,
      right: 20,
      zIndex: 9999
    },
    'inline': {
      position: 'relative'
    }
  };
  
  const statusConfig = {
    idle: {
      bg: 'transparent',
      text: '',
      icon: null
    },
    saving: {
      bg: '#1e293b',
      text: progress?.phase === 'photos' 
        ? `Saving photos (${progress.done}/${progress.total})...`
        : 'Saving...',
      icon: (
        <div style={{
          width: 16,
          height: 16,
          border: '2px solid #64748b',
          borderTopColor: '#3b82f6',
          borderRadius: '50%',
          animation: 'saveStatusSpin 0.8s linear infinite'
        }} />
      )
    },
    saved: {
      bg: '#065f46',
      text: 'Saved',
      icon: <span style={{ fontSize: 14 }}>✓</span>
    },
    error: {
      bg: '#991b1b',
      text: 'Save failed',
      icon: <span style={{ fontSize: 14 }}>✕</span>
    }
  };
  
  const config = statusConfig[status] || statusConfig.idle;
  
  if (!config.text) return null;
  
  return (
    <>
      <style>{`
        @keyframes saveStatusSpin {
          to { transform: rotate(360deg); }
        }
        @keyframes saveStatusFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      
      <div 
        className={className}
        style={{
          ...positionStyles[position],
          background: config.bg,
          color: 'white',
          padding: '8px 16px',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 14,
          fontWeight: 500,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          animation: 'saveStatusFadeIn 0.2s ease-out'
        }}
      >
        {config.icon}
        <span>{config.text}</span>
        
        {/* Progress bar for photo saving */}
        {status === 'saving' && progress?.total > 0 && (
          <div style={{
            width: 60,
            height: 4,
            background: '#334155',
            borderRadius: 2,
            overflow: 'hidden',
            marginLeft: 8
          }}>
            <div style={{
              height: '100%',
              width: `${(progress.done / progress.total) * 100}%`,
              background: '#3b82f6',
              transition: 'width 0.2s ease-out'
            }} />
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Compact inline version for tight spaces
 */
export function SaveStatusBadge({ status, className = '' }) {
  if (status === 'idle') return null;
  
  const badges = {
    saving: { text: 'Saving...', color: '#64748b' },
    saved: { text: '✓ Saved', color: '#059669' },
    error: { text: '✕ Failed', color: '#dc2626' }
  };
  
  const badge = badges[status];
  if (!badge) return null;
  
  return (
    <span 
      className={className}
      style={{
        fontSize: 12,
        color: badge.color,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4
      }}
    >
      {status === 'saving' && (
        <span style={{
          width: 10,
          height: 10,
          border: '1.5px solid currentColor',
          borderTopColor: 'transparent',
          borderRadius: '50%',
          animation: 'saveStatusSpin 0.8s linear infinite',
          display: 'inline-block'
        }} />
      )}
      {badge.text}
    </span>
  );
}
